import { fmtNum } from '../ui/ListFoot';
import { ShowHide } from '../ui/ShowHide';
import { FilterChip, RemovableChip } from '../ui/bits';
import { Popover } from '../ui/Popover';
import { useWide } from '../shell/hooks';
import { keep as remember, recall } from '../lib/remember';
import { useState } from 'preact/hooks';
import { activeChips, filterCount, SECTION_MIN_ROWS, SECTIONS, setMode, toggleChip, type FilterState, type Group, type GroupKey, type Section } from './filters';

// Shelves' filters on screen (Part 3; Kristi's mockup round 2, Oct 28): the Filters pill in the
// header, filled while any are on; folded, the chips that are on (✕ each), Clear all and the count;
// opened, a full-width panel with each group's name in a left column, in Kristi's order.
// Consistency pass (R4, Kristi Nov 1): the pill is a Secondary pill "Filters ▾", filled teal and
// counting in brackets while any are on ("Filters [3] ▾"); on a laptop the panel drops from the
// pinned bar, on a phone it opens as a full-height sheet. Queue, Revisit, Recently added and
// Owned copies use these same two pieces.
// Filters review (Kristi, Nov 2): the foot always reads "Clear all · Show N books" (F7); a long
// panel reads in groups, as headings on a laptop and iPad and as groups that fold on a phone (F16).

const num = fmtNum;
const nounText = (n: number, noun: [string, string]) => (n === 1 ? `1 ${noun[0]}` : `${num(n)} ${noun[1]}`);

/** The header's part: Filters [3] ▾, then (folded) the chips that are on, Clear all, "· 212 books". */
export function FilterBar({
  groups,
  state,
  open,
  onToggle,
  onChange,
  showing,
  noun = ['book', 'books'],
}: {
  groups: Group[];
  state: FilterState;
  open: boolean;
  onToggle: () => void;
  onChange: (s: FilterState) => void;
  /** Books matching while the list is narrowed (by Filters or the page's chips); null when it isn't. */
  showing: number | null;
  /** What the count counts: books (Shelves) or copies (Owned copies). */
  noun?: [string, string];
}) {
  const n = filterCount(state);
  const chips = activeChips(groups, state);
  return (
    <>
      <button
        type="button"
        class={`btn-secondary pill-menu filters-toggle${n ? ' on' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="shelf-filters"
        onClick={onToggle}
      >
        {n ? `Filters [${n}]` : 'Filters'} <span aria-hidden="true">▾</span>
      </button>
      {/* What's on, as filled chips with ✕, then Clear all (R3 P6, Kristi Nov 2). While the panel is open it shows them itself. */}
      {!open
        ? chips.map((c) => <RemovableChip key={`${c.group}:${c.id}`} label={c.label} hide={c.hide} onRemove={() => onChange(toggleChip(state, c.group, c.id))} />)
        : null}
      {n && !open ? (
        <button type="button" class="btn-link filters-clear" onClick={() => onChange({})}>
          Clear all
        </button>
      ) : null}
      {/* The result whenever the list is narrowed, by Filters or by the page's own chips (inventory, Nov 2). */}
      {showing !== null ? <span class="filters-count">· {nounText(showing, noun)}</span> : null}
    </>
  );
}

/** A section's fold on the phone, remembered on this device (F16; Kristi, Nov 2). */
const foldKey = (s: Section) => `filters:section:${s}`;

/** The opened panel. Inline under the pinned bar on a laptop; a full-height sheet on a phone. */
export function FilterPanel({
  groups,
  state,
  onChange,
  onClose,
  showing,
  noun = ['book', 'books'],
}: {
  groups: Group[];
  state: FilterState;
  onChange: (s: FilterState) => void;
  onClose: () => void;
  /** What "Show N books" shows: the list as it would be with these filters. */
  showing: number;
  /** What the count counts: books (Shelves) or copies (Owned copies). */
  noun?: [string, string];
}) {
  const wide = useWide();
  const [more, setMore] = useState<Set<GroupKey>>(new Set());
  // Rows with nothing to pick (every chip at [0]) don't show, unless they explain why (Subgenre).
  const live = groups.filter((g) => g.chips.length || g.empty);
  const rowsOf = (list: Group[]) => {
    const rows: { row: string; groups: Group[] }[] = [];
    for (const g of list) {
      const r = rows.find((x) => x.row === g.row);
      if (r) r.groups.push(g);
      else rows.push({ row: g.row, groups: [g] });
    }
    return rows;
  };
  const allRows = rowsOf(live);
  const sectioned = allRows.length >= SECTION_MIN_ROWS && live.every((g) => g.section);
  const sections = sectioned ? SECTIONS.map(([key, name]) => ({ key, name, rows: rowsOf(live.filter((g) => g.section === key)) })).filter((s) => s.rows.length) : [];
  const hasOn = (s: { rows: { groups: Group[] }[] }) => s.rows.some((r) => r.groups.some((g) => state[g.key]?.on.length));
  // The phone's folds (Kristi, Nov 2): each group as you left it on this device; the first time,
  // About the book is open; a group with a filter on always opens.
  const [openSecs, setOpenSecs] = useState<Set<Section>>(
    () => new Set(sections.filter((s) => hasOn(s) || recall(foldKey(s.key), s.key === 'book')).map((s) => s.key)),
  );
  const setSec = (k: Section, open: boolean) => {
    const next = new Set(openSecs);
    if (open) next.add(k);
    else next.delete(k);
    setOpenSecs(next);
    remember(foldKey(k), open);
  };
  const setAll = (open: boolean) => {
    setOpenSecs(open ? new Set(sections.map((s) => s.key)) : new Set());
    for (const s of sections) remember(foldKey(s.key), open);
  };
  const allOpen = sections.every((s) => openSecs.has(s.key));

  const row = (r: { row: string; groups: Group[] }) => (
    <div key={r.row} class="filters-row" role="group" aria-label={r.row}>
      <span class="filters-label">{r.row}</span>
      <div class="filters-chips">
        {r.groups.map((g, i) => {
          const s = state[g.key] ?? { mode: 'show', on: [] };
          const avail = g.chips;
          const all = more.has(g.key) || !g.firstShown ? avail : avail.slice(0, g.firstShown);
          // Chips that are on always show, even beyond the first ones.
          const shown = [...all, ...avail.filter((c) => s.on.includes(c.id) && !all.includes(c))];
          return (
            <span key={g.key} class="filters-group">
              {i > 0 ? <span class="filters-sep" aria-hidden="true" /> : null}
              {g.hideable && avail.length ? <ShowHide mode={s.mode} label={g.label} onPick={(m) => onChange(setMode(state, g.key, m))} /> : null}
              {!avail.length && g.empty ? <span class="filters-empty">{g.empty}</span> : null}
              {shown.map((c) => {
                const on = s.on.includes(c.id);
                return (
                  <FilterChip
                    key={c.id}
                    label={on && s.mode === 'hide' ? <s>{c.label}</s> : c.label}
                    count={c.count}
                    on={on}
                    need={c.needs ? c.tip ?? 'Needs you' : undefined}
                    worth={c.worth ? c.tip ?? 'Worth a look' : undefined}
                    onToggle={() => onChange(toggleChip(state, g.key, c.id))}
                  />
                );
              })}
              {g.firstShown && avail.length > g.firstShown ? (
                more.has(g.key) ? (
                  // Back to the first ones (Kristi, Oct 28); chips that are on stay showing.
                  <button type="button" class="btn-link" onClick={() => setMore(new Set([...more].filter((k) => k !== g.key)))}>
                    Show fewer
                  </button>
                ) : (
                  <button type="button" class="btn-link" onClick={() => setMore(new Set([...more, g.key]))}>
                    {g.moreLabel ? g.moreLabel(avail.length - g.firstShown) : `+ ${avail.length - g.firstShown} more`}
                  </button>
                )
              ) : null}
            </span>
          );
        })}
      </div>
    </div>
  );

  // A folded group says what's on in it (small filled pills), else which rows it holds, so nothing that's on is hidden.
  const foldSummary = (s: (typeof sections)[number]) => {
    const on = activeChips(s.rows.flatMap((r) => r.groups), state);
    return on.length ? (
      <span class="filters-sec-on">
        {on.map((c) => (
          <span key={`${c.group}:${c.id}`} class="filters-sec-pill">
            {c.hide ? `Hide: ${c.label}` : c.label}
          </span>
        ))}
      </span>
    ) : (
      <span class="filters-sec-rows">{s.rows.map((r) => r.row).join(' · ')}</span>
    );
  };

  const body = !sectioned
    ? allRows.map(row)
    : wide
      ? sections.map((s) => (
          <section key={s.key} class="filters-sec" aria-label={s.name}>
            <h3 class="filters-sec-head">{s.name}</h3>
            {s.rows.map(row)}
          </section>
        ))
      : [
          <div key="all" class="filters-sec-all">
            <button type="button" class="btn-link" onClick={() => setAll(!allOpen)}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>,
          ...sections.map((s) => {
            const open = openSecs.has(s.key);
            return (
              <section key={s.key} class={`filters-sec folding${open ? ' open' : ''}`} aria-label={s.name}>
                <button type="button" class="filters-sec-head" aria-expanded={open} onClick={() => setSec(s.key, !open)}>
                  <span class="filters-sec-name">
                    <span aria-hidden="true">{open ? '▾' : '▸'}</span> {s.name}
                  </span>
                  {open ? null : foldSummary(s)}
                </button>
                {open ? s.rows.map(row) : null}
              </section>
            );
          }),
        ];

  const panel = (
    <div id="shelf-filters" class={`filters-panel${sectioned ? ' sectioned' : ''}`} role="region" aria-label="Filters">
      {body}
      <div class="filters-foot">
        <span class="filters-foot-buttons">
          <button type="button" class="btn-link" onClick={() => onChange({})}>
            Clear all
          </button>
          {/* Always the result, never "Done" (F7, Kristi Nov 2). */}
          <button type="button" class="btn-main" onClick={onClose}>
            Show {nounText(showing, noun)}
          </button>
        </span>
      </div>
    </div>
  );
  if (wide) return panel;
  return (
    <Popover open onClose={onClose} label="Filters" sheetTitle="Filters" full>
      {panel}
    </Popover>
  );
}
